/**
 * This function calculates if an event should be to send to sentry
 * or if we want to exclude it.
 *
 * @param {Object} event Sentry event
 * @param {Object} eventMatcher Matcher settings
 * @param {string} [eventMatcher.type] The event type. For example: TypeError
 * @param {string} [eventMatcher.value] The event message. For example: Cannot read properties of undefined
 * @param {string} [eventMatcher.message] The event message in case is not an exception. For example: Cannot read properties of undefined
 * @param {string} [eventMatcher.file] The source file path or string. For example: http://bentobox.com/file.js or just file.js
 * @param {string} [eventMatcher.missingStackTrace] If set to true, the expectation is that the event doesn't have a stack trace, if set to false (the default), the event must have a stacktrace.
 *
 * @returns boolean
 */
export function excludeEvent(event: any, eventMatcher: any) {
  // Usage restrictions
  const matcherProperties = Object.getOwnPropertyNames(eventMatcher);
  if (
    matcherProperties.includes('file') &&
    matcherProperties.includes('missingStackTrace')
  ) {
    throw Error(
      'This two properties can not be set at the same time, please only use one: "file", "missingStackTrace"'
    );
  }
  try {
    if (event.exception) {
      let typeAndValueMatches = false;
      let fileMatches = false;
      let passesStackTraceCheck = true;
      const sentryExcept =
        event.exception.values[event.exception.values.length - 1];
      if (eventMatcher.missingStackTrace && sentryExcept.stacktrace) {
        passesStackTraceCheck = false;
      }
      if (!eventMatcher.missingStackTrace && !sentryExcept.stacktrace) {
        passesStackTraceCheck = false;
      }
      if (eventMatcher.type && eventMatcher.value) {
        const typeMatches = sentryExcept.type.includes(eventMatcher.type);
        const valueMatches = sentryExcept.value.includes(eventMatcher.value);
        typeAndValueMatches = typeMatches && valueMatches;
      }
      if (eventMatcher.file) {
        const frame =
          sentryExcept.stacktrace?.frames[
            sentryExcept.stacktrace?.frames.length - 1
          ];
        if (frame) {
          const filenameMatches = (frame.filename ?? '').includes(
            eventMatcher.file
          );
          const absPathMatches = (frame.abs_path ?? '').includes(
            eventMatcher.file
          );
          fileMatches = filenameMatches || absPathMatches;
        }
        return typeAndValueMatches && fileMatches;
      }
      return typeAndValueMatches && passesStackTraceCheck;
    }
    if (eventMatcher.message && event.message) {
      return event.message.includes(eventMatcher.message);
    }
  } catch (error) {
    return false;
  }
  return false;
}
/**
 * If you want to add a new matcher,
 * please make sure all test pass on "sentryUtils.test.js" file.
 *
 * Also, don't forget to add an example of the event on "excludeEventRealEvents.js" file,
 * The test will automatically pick it up and run the test applying the matchers.
 */
export const excludeEventMatcherList = [
  {
    // https://bentobox.sentry.io/issues/5133015178/?project=6633349
    type: 'Error',
    message: 'Failed to load Nextable Config'
  },
  {
    // https://bentobox.sentry.io/issues/5891083280/events/latest/?environment=production&project=6633349&referrer=latest-event
    type: 'ZodError',
    value:
      '[\n  {\n    "validation": "email",\n    "code": "invalid_string",\n    "message": "Invalid email",\n    "path": [\n      "booking",\n      "email"\n    ]\n  }\n]',
    file: 'zod/lib/index.mjs'
  },
  {
    // https://bentobox.sentry.io/issues/5891083280/events/latest/?environment=production&project=6633349&referrer=latest-event
    type: 'ZodError',
    value:
      'fetchBookingDetails:  [\n  {\n    "validation": "email",\n    "code": "invalid_string",\n    "message": "Invalid email",\n    "path": [\n      "booking",\n      "email"\n    ]\n  }\n]',
    file: 'zod/lib/index.mjs'
  },
  {
    // https://bentobox.sentry.io/issues/5891083280/events/latest/?environment=production&project=6633349&referrer=latest-event
    type: 'ZodError',
    value:
      '[ { "validation": "email", "code": "invalid_string", "message": "Invalid email", "path": [ "booking", "email" ] } ]',
    file: 'zod/lib/index.mjs'
  },
  {
    // https://bentobox.sentry.io/issues/5891083280/events/latest/?environment=production&project=6633349&referrer=latest-event
    type: 'ZodError',
    value: `[
  {
    "validation": "email",
    "code": "invalid_string",
    "message": "Invalid email",
    "path": [
      "booking",
      "email"
    ]
  }
]
`,
    file: 'zod/lib/index.mjs'
  },
  {
    // https://bentobox.sentry.io/issues/6067678157/
    type: 'TypeError',
    value: "undefined is not an object (evaluating 't.ssresp')",
    file: 'reservations-app-prod-radware.getbento.com'
  },
  {
    // https://bentobox.sentry.io/issues/4551263084/events/b2a1063d459a421eb0c0af8c46ac024b/
    type: 'UnhandledRejection',
    value: 'Non-Error promise rejection captured with value: Object Not Found',
    missingStackTrace: true
  },
  {
    // https://bentobox.sentry.io/issues/5994477921
    type: 'TypeError',
    value: 'Load failed',
    missingStackTrace: true
  },
  {
    // https://bentobox.sentry.io/issues/5978959189/
    type: 'Error',
    value: 'Failed to load static props',
    file: 'node_modules/next/dist/shared/lib/router/router.js'
  },
  {
    // https://bentobox.sentry.io/issues/6273832787/events/831fcefe2cbf42b4bfd166098ed2e9f6/
    type: 'Error',
    value: 'Failed to load static props',
    file: 'src/shared/lib/router/router.ts'
  },
  {
    // https://bentobox.sentry.io/issues/6273430393/events/21f20734d7ba4740835cef7509963d0a/
    type: 'Error',
    value: 'Abort fetching component for route: "/404"',
    file: 'src/shared/lib/router/router.ts'
  },
  {
    // https://bentobox.sentry.io/issues/6273576009
    type: 'Error',
    value: 'Failed to load script: /make-a-reservation/_next/static/chunks'
  }
];

export function beforeSend(event: any) {
  // Check if the event matches any exclusion criteria
  const ignoreEvent = excludeEventMatcherList.some(matcher =>
    excludeEvent(event, matcher)
  );

  if (ignoreEvent) {
    // Extract exception details or fallback to a message
    const sentryExcept = event.exception?.values?.at(-1) || {
      type: 'Message',
      value: event.message
    };

    // Log the exception for debugging
    console.warn(
      `[reservations] Unhandled Exception: [${sentryExcept.type}][${sentryExcept.value}]`,
      { eventId: event.event_id, tags: event.tags }
    );

    // Return null to ignore the event
    return null;
  }

  // Return the event if it doesn't match exclusion criteria
  return event;
}
